/**
 * Admin Page Entry Point
 * Main entry for the admin dashboard
 */

import Alpine from 'alpinejs'
import '../styles/main.css'

import { initSentry } from './config/sentry'

initSentry()

// Import stores
import { registerAuthStore } from './stores/auth.store'
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in ReturnType<typeof createReportsStore>
import { registerReportsStore, createReportsStore } from './stores/reports.store'
import { registerToastsStore } from './stores/toasts.store'

// Import components
import { registerModalComponents } from './components/modal'
import { registerToastComponents } from './components/toast'
import { registerFilterComponents } from './components/filters'
import { registerMapComponents } from './components/map'

// Import services and types
import type { Report, ReportStatus } from './types'
import { formatDate, formatRelativeTime, formatStatus, getStatusClass } from './utils/format'
import {
  HELP_CONTENT,
  hasNewUpdates,
  setLastSeenVersion,
  getChangeTypeBadgeClass,
  type ChangelogEntry,
  type Guide,
} from './data/help-content'

// ============================================================================
// Admin Dashboard Component
// ============================================================================

type AdminView = 'pending' | 'dashboard' | 'geographic' | 'adopters'

interface AdminDashboardData {
  // View state
  activeView: AdminView
  sidebarOpen: boolean
  loading: boolean
  error: string | null

  // Modal state
  showRejectModal: boolean
  rejectReportId: string | null
  rejectReason: string

  showDetailModal: boolean
  selectedReport: Report | null

  // Stats
  stats: {
    total: number
    pending: number
    new: number
    investigating: number
    resolved: number
    closed: number
  } | null

  // Methods
  init(): Promise<void>
  setView(view: AdminView): void
  approveReport(id: string): Promise<void>
  openRejectModal(id: string): void
  rejectReport(): Promise<void>
  closeRejectModal(): void
  updateStatus(id: string, status: ReportStatus): Promise<void>
  deleteReport(id: string): Promise<void>
  openDetailModal(report: Report): void
  closeDetailModal(): void
  formatDate(date: unknown): string
  formatRelativeTime(date: unknown): string
  formatStatus(status: ReportStatus): string
  getStatusClass(status: ReportStatus): string
}

function adminDashboard(): AdminDashboardData {
  return {
    activeView: 'pending',
    sidebarOpen: false,
    loading: true,
    error: null,

    showRejectModal: false,
    rejectReportId: null,
    rejectReason: '',

    showDetailModal: false,
    selectedReport: null,

    stats: null,

    async init(): Promise<void> {
      // Initialize auth store
      const authStore = Alpine.store('auth') as { init(): void }
      authStore.init()

      // Initialize reports store
      const reportsStore = Alpine.store('reports')
      reportsStore.init()

      // Fetch pending reports
      await reportsStore.fetchPending()

      // Load stats
      await reportsStore.loadStats()
      this.stats = reportsStore.stats

      // Load countries for filters
      await reportsStore.loadCountries()

      this.loading = false
    },

    setView(view: AdminView): void {
      this.activeView = view
      this.sidebarOpen = false

      const reportsStore = Alpine.store('reports')

      // Fetch appropriate data for view
      if (view === 'pending') {
        void reportsStore.fetchPending()
      } else if (view === 'dashboard') {
        void reportsStore.fetchReports()
      }
    },

    async approveReport(id: string): Promise<void> {
      const reportsStore = Alpine.store('reports')
      const toasts = Alpine.store('toasts') as {
        success(msg: string): void
        error(msg: string): void
      }

      try {
        await reportsStore.updateStatus(id, 'new')
        toasts.success('Report approved successfully')
      } catch (error) {
        toasts.error('Failed to approve report')
        console.error('Approve error:', error)
      }
    },

    openRejectModal(id: string): void {
      this.rejectReportId = id
      this.rejectReason = ''
      this.showRejectModal = true
    },

    async rejectReport(): Promise<void> {
      if (!this.rejectReportId) {
        return
      }

      const reportsStore = Alpine.store('reports')
      const toasts = Alpine.store('toasts') as {
        success(msg: string): void
        error(msg: string): void
      }

      try {
        await reportsStore.updateStatus(this.rejectReportId, 'closed', this.rejectReason)
        toasts.success('Report rejected')
        this.closeRejectModal()
      } catch (error) {
        toasts.error('Failed to reject report')
        console.error('Reject error:', error)
      }
    },

    closeRejectModal(): void {
      this.showRejectModal = false
      this.rejectReportId = null
      this.rejectReason = ''
    },

    async updateStatus(id: string, status: ReportStatus): Promise<void> {
      const reportsStore = Alpine.store('reports')
      const toasts = Alpine.store('toasts') as {
        success(msg: string): void
        error(msg: string): void
      }

      try {
        await reportsStore.updateStatus(id, status)
        toasts.success(`Status updated to ${formatStatus(status)}`)
      } catch (error) {
        toasts.error('Failed to update status')
        console.error('Status update error:', error)
      }
    },

    async deleteReport(id: string): Promise<void> {
      if (!confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
        return
      }

      const reportsStore = Alpine.store('reports')
      const toasts = Alpine.store('toasts') as {
        success(msg: string): void
        error(msg: string): void
      }

      try {
        await reportsStore.remove(id)
        toasts.success('Report deleted')
        this.closeDetailModal()
      } catch (error) {
        toasts.error('Failed to delete report')
        console.error('Delete error:', error)
      }
    },

    openDetailModal(report: Report): void {
      this.selectedReport = report
      this.showDetailModal = true
    },

    closeDetailModal(): void {
      this.showDetailModal = false
      this.selectedReport = null
    },

    formatDate(date: unknown): string {
      return formatDate(date as Date)
    },

    formatRelativeTime(date: unknown): string {
      return formatRelativeTime(date as Date)
    },

    formatStatus(status: ReportStatus): string {
      return formatStatus(status)
    },

    getStatusClass(status: ReportStatus): string {
      return getStatusClass(status)
    },
  }
}

// ============================================================================
// Report Table Component
// ============================================================================

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function reportTable() {
  return {
    sortField: 'timestamp',
    sortDirection: 'desc' as 'asc' | 'desc',
    selectedIds: new Set<string>(),

    sort(field: string): void {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDirection = 'desc'
      }
    },

    toggleSelect(id: string): void {
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id)
      } else {
        this.selectedIds.add(id)
      }
    },

    selectAll(reports: Report[]): void {
      if (this.selectedIds.size === reports.length) {
        this.selectedIds.clear()
      } else {
        reports.forEach((r) => this.selectedIds.add(r.id))
      }
    },

    isSelected(id: string): boolean {
      return this.selectedIds.has(id)
    },

    get hasSelection(): boolean {
      return this.selectedIds.size > 0
    },

    async bulkUpdateStatus(status: ReportStatus): Promise<void> {
      const reportsStore = Alpine.store('reports')
      const toasts = Alpine.store('toasts') as {
        success(msg: string): void
        error(msg: string): void
      }

      try {
        for (const id of this.selectedIds) {
          await reportsStore.updateStatus(id, status)
        }
        toasts.success(`Updated ${String(this.selectedIds.size)} reports`)
        this.selectedIds.clear()
      } catch (error) {
        toasts.error('Failed to update some reports')
        console.error('Bulk update error:', error)
      }
    },
  }
}

// ============================================================================
// Auth Guard Component
// ============================================================================

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function authGuard() {
  return {
    get isLoading(): boolean {
      const auth = Alpine.store('auth') as { loading: boolean }
      return auth.loading
    },

    get isAdmin(): boolean {
      const auth = Alpine.store('auth') as { isAdmin: boolean }
      return auth.isAdmin
    },

    get isAuthenticated(): boolean {
      const auth = Alpine.store('auth') as { user: unknown }
      return auth.user !== null
    },

    async signIn(): Promise<void> {
      const auth = Alpine.store('auth') as { signIn(): Promise<void> }
      await auth.signIn()
    },

    async signOut(): Promise<void> {
      const auth = Alpine.store('auth') as { signOut(): Promise<void> }
      await auth.signOut()
    },
  }
}

// ============================================================================
// Help Center Component
// ============================================================================

interface HelpCenterData {
  isOpen: boolean
  activeTab: 'changelog' | 'admin' | 'user'
  hasNewUpdates: boolean
  changelog: ChangelogEntry[]
  adminGuides: Guide[]
  userGuides: Guide[]

  init(): void
  open(): void
  close(): void
  setTab(tab: 'changelog' | 'admin' | 'user'): void
  markAsSeen(): void
  getChangeTypeBadgeClass(type: string): string
}

function helpCenter(): HelpCenterData {
  return {
    isOpen: false,
    activeTab: 'changelog',
    hasNewUpdates: false,
    changelog: HELP_CONTENT.changelog,
    adminGuides: HELP_CONTENT.guides.admin,
    userGuides: HELP_CONTENT.guides.user,

    init(): void {
      this.hasNewUpdates = hasNewUpdates()
    },

    open(): void {
      this.isOpen = true
      this.markAsSeen()
    },

    close(): void {
      this.isOpen = false
    },

    setTab(tab: 'changelog' | 'admin' | 'user'): void {
      this.activeTab = tab
    },

    markAsSeen(): void {
      setLastSeenVersion(HELP_CONTENT.version)
      this.hasNewUpdates = false
    },

    getChangeTypeBadgeClass(type: string): string {
      return getChangeTypeBadgeClass(type)
    },
  }
}

// ============================================================================
// Initialize Application
// ============================================================================

// Register stores
registerAuthStore()
registerReportsStore()
registerToastsStore()

// Register components
registerModalComponents()
registerToastComponents()
registerFilterComponents()
registerMapComponents()

// Register page components
Alpine.data('adminDashboard', adminDashboard)
Alpine.data('reportTable', reportTable)
Alpine.data('authGuard', authGuard)
Alpine.data('helpCenter', helpCenter)

// Start Alpine
Alpine.start()
